<style scoped>
.ia-map-root {
	position: absolute;
	inset: 0;
}

/* Ensure the map fills the full-viewport wrapper. */
.ia-map-root :deep(.maplibregl-map) {
	width: 100%;
	height: 100%;
}

.ia-map-root :deep(.maplibregl-canvas) {
	width: 100%;
	height: 100%;
}

/*
 * Bottom centre: the top corners hold the page's panel toggles and the
 * floating site list / trap chart.
 */
.ia-legend {
	position: absolute;
	left: 50%;
	bottom: 12px;
	transform: translateX(-50%);
	z-index: 10;
	font-size: 0.8rem;
}

.ia-legend-title {
	font-weight: 600;
	margin-bottom: 0.25rem;
}

.ia-legend-swatch {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 3px;
}

/* Same stops as the `ia-hex-fill` layer's fill-color ramp. */
.ia-legend-gradient {
	width: 140px;
	height: 10px;
	border-radius: 3px;
	background: linear-gradient(
		to right,
		#cfe2ff 0%,
		#9ec5fe 25%,
		#6ea8fe 50%,
		#3d8bfd 75%,
		#0a58ca 100%
	);
}
</style>

<template>
	<div class="ia-map-root">
		<mgl-map
			:map-style="mapStyleURL(ALIDADE_SMOOTH)"
			:bounds="bounds"
			:fit-bounds-options="fitOptions"
			@map:click="onMapClick"
		>
			<!-- Larval findings per H3 cell for the whole district. -->
			<mgl-geo-json-source source-id="ia-hex" :data="H3_CELLS">
				<mgl-fill-layer
					layer-id="ia-hex-fill"
					:filter="hexFilter"
					:paint="{
						'fill-color': [
							'interpolate',
							['linear'],
							['get', 'count'],
							1,
							'#cfe2ff',
							2,
							'#9ec5fe',
							4,
							'#6ea8fe',
							6,
							'#3d8bfd',
							8,
							'#0a58ca',
						],
						'fill-opacity': 0.55,
					}"
				/>
				<mgl-line-layer
					layer-id="ia-hex-outline"
					:filter="hexFilter"
					:paint="{
						'line-color': '#0d6efd',
						'line-width': 1,
						'line-opacity': 0.5,
					}"
				/>
			</mgl-geo-json-source>

			<!-- Halo for the selected site, drawn under the site markers. -->
			<mgl-geo-json-source source-id="ia-selected" :data="selectedFeature">
				<mgl-circle-layer
					layer-id="ia-site-selected"
					:paint="{
						'circle-radius': [
							'interpolate',
							['linear'],
							['zoom'],
							8,
							11,
							13,
							16,
						],
						'circle-color': 'rgba(13,110,253,0.15)',
						'circle-stroke-color': '#0d6efd',
						'circle-stroke-width': 3,
					}"
				/>
			</mgl-geo-json-source>

			<!-- One marker per found site, coloured by its most advanced stage. -->
			<mgl-geo-json-source source-id="ia-sites" :data="siteFeatures">
				<mgl-circle-layer
					layer-id="ia-site-points"
					:paint="{
						'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 5, 13, 9],
						'circle-color': ['get', 'color'],
						'circle-stroke-color': '#ffffff',
						'circle-stroke-width': 1.5,
						'circle-opacity': 0.95,
					}"
					@mouseenter="onSiteMouseEnter"
					@mouseleave="onSiteMouseLeave"
				/>
			</mgl-geo-json-source>

			<!-- The "current location" the trap chart reads from. -->
			<mgl-geo-json-source source-id="ia-location" :data="locationFeature">
				<mgl-circle-layer
					layer-id="ia-location-marker"
					:paint="{
						'circle-radius': 6,
						'circle-color': '#0d6efd',
						'circle-stroke-color': '#ffffff',
						'circle-stroke-width': 3,
					}"
				/>
			</mgl-geo-json-source>
		</mgl-map>

		<div class="ia-legend bg-white rounded shadow-sm border px-3 py-2">
			<div class="d-flex gap-4">
				<div>
					<div class="ia-legend-title">Larvae stage</div>
					<div class="d-flex flex-column gap-1">
						<div
							v-for="stage in LARVAE_STAGES"
							:key="stage"
							class="d-flex align-items-center gap-2"
						>
							<span
								class="ia-legend-swatch"
								:style="{ background: LARVAE_STAGE_COLORS[stage] }"
							></span>
							<span>{{ stage }}</span>
						</div>
					</div>
				</div>

				<div>
					<div class="ia-legend-title">IA larval findings</div>
					<div class="d-flex flex-column gap-1">
						<div class="ia-legend-gradient"></div>
						<div class="d-flex justify-content-between text-muted">
							<span>1</span>
							<span>8+</span>
						</div>
						<div class="text-muted">
							Hex cell = larval findings (zoom changes resolution)
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import "maplibre-gl/dist/maplibre-gl.css";

import { computed } from "vue";
import maplibregl from "maplibre-gl";
import type { FilterSpecification, MapLayerMouseEvent } from "maplibre-gl";
import type { Feature, FeatureCollection, Point } from "geojson";
import {
	MglCircleLayer,
	MglFillLayer,
	MglGeoJsonSource,
	MglLineLayer,
	MglMap,
	type MglEvent,
} from "vue-maplibre-gl";

import { ALIDADE_SMOOTH } from "@/map/constants";
import { mapStyleURL } from "@/map/util";
import {
	H3_CELLS,
	LARVAE_STAGES,
	LARVAE_STAGE_COLORS,
	SITES,
	type InvasiveAedesSite,
	type LarvaeStage,
} from "@/mock/invasive-aedes";

interface Props {
	selectedSiteId: string | null;
	location: { latitude: number; longitude: number };
}

interface Emits {
	(e: "select-site", siteId: string): void;
	(e: "select-location", latitude: number, longitude: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Mirror of ts/components/MapAggregate.vue's zoom/resolution filter: one hex
// resolution per zoom band, so something is always drawn at fractional zoom.
// Checked with `satisfies` rather than annotated, so the tuple shape stays the
// one the layers' filter prop accepts (that union predates style-spec's `null`
// expression inputs, which a plain FilterSpecification annotation brings in).
const hexFilter = [
	"==",
	["get", "resolution"],
	["case", ["<", ["zoom"], 10], 7, ["<", ["zoom"], 11], 8, 9],
] satisfies FilterSpecification;

/** Opening view over every found site. */
const bounds = new maplibregl.LngLatBounds(
	[
		Math.min(...SITES.map((site) => site.longitude)),
		Math.min(...SITES.map((site) => site.latitude)),
	],
	[
		Math.max(...SITES.map((site) => site.longitude)),
		Math.max(...SITES.map((site) => site.latitude)),
	],
);

// The floating site list and trap chart cover the map edges.
const fitOptions = {
	padding: { top: 70, bottom: 70, left: 360, right: 340 },
	duration: 800,
};

type SiteProperties = {
	id: string;
	name: string;
	color: string;
	count: number;
};

/**
 * Marker colour = the most advanced stage found at the site, i.e. the latest
 * entry of `site.stages` in `LARVAE_STAGES` order. The legend and the site list
 * read the same per-stage colours.
 */
function markerColor(stages: LarvaeStage[]): string {
	for (let i = LARVAE_STAGES.length - 1; i >= 0; i--) {
		if (stages.includes(LARVAE_STAGES[i])) {
			return LARVAE_STAGE_COLORS[LARVAE_STAGES[i]];
		}
	}
	return LARVAE_STAGE_COLORS[LARVAE_STAGES[0]];
}

function siteFeature(site: InvasiveAedesSite): Feature<Point, SiteProperties> {
	return {
		type: "Feature",
		geometry: {
			type: "Point",
			coordinates: [site.longitude, site.latitude],
		},
		properties: {
			id: site.id,
			name: site.name,
			color: markerColor(site.stages),
			count: site.count,
		},
	};
}

const siteFeatures = computed<FeatureCollection<Point, SiteProperties>>(() => ({
	type: "FeatureCollection",
	features: SITES.map(siteFeature),
}));

/** Exactly zero or one point: the site the page has selected. */
const selectedFeature = computed<FeatureCollection<Point, SiteProperties>>(
	() => {
		const site = SITES.find((s) => s.id === props.selectedSiteId);
		return {
			type: "FeatureCollection",
			features: site ? [siteFeature(site)] : [],
		};
	},
);

/** Always one point: where the trap chart is centred. */
const locationFeature = computed<FeatureCollection<Point>>(() => ({
	type: "FeatureCollection",
	features: [
		{
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: [props.location.longitude, props.location.latitude],
			},
			properties: null,
		},
	],
}));

function onSiteMouseEnter(e: MapLayerMouseEvent) {
	e.target.getCanvas().style.cursor = "pointer";
}

function onSiteMouseLeave(e: MapLayerMouseEvent) {
	e.target.getCanvas().style.cursor = "";
}

// Sites win over hexes: a click on a marker selects the site, any other click
// on a hex moves the "current location" the trap chart follows.
function onMapClick(e: MglEvent<"click">) {
	const ev = e.event;
	const m = e.map;

	const siteHits = m.queryRenderedFeatures(ev.point, {
		layers: ["ia-site-points"],
	});
	if (siteHits.length > 0) {
		emit("select-site", String(siteHits[0].properties?.id));
		return;
	}

	const hexHits = m.queryRenderedFeatures(ev.point, {
		layers: ["ia-hex-fill"],
	});
	if (hexHits.length > 0) {
		emit("select-location", ev.lngLat.lat, ev.lngLat.lng);
	}
}
</script>
